// i18n
import { jaLocale, translateInComponent } from 'infras/i18n';
import translations from './i18n.json';

import styled from '@emotion/styled';
import Logo from 'presentations/views/components/atoms/Logo';
import {
  mediaMax,
  mediaMin,
  fontSize,
  fontWeight,
  spacing,
} from 'presentations/views/styles';
import {
  homePath,
  privacyPolicyPath,
  termsPath,
  tokushohoPath,
  aboutPath,
  securityPolicyPath,
} from 'presentations/routes/path';
import Link from 'presentations/views/components/atoms/Link';
import { black, gray01 } from 'presentations/views/styles/color';
import { formatToYear } from 'infras/datetime';
import { memo } from 'react';
import { isInElectron } from 'presentations/views/helpers/electron';
import { useRouter } from 'next/router';

interface Props {
  isHidden?: boolean;
  isThin?: boolean;
}

const Component = memo((props: Props) => {
  if (isInElectron()) return null;
  if (props.isHidden) return null;
  const t = translateInComponent(translations);
  const copyRight = <>© {formatToYear()} Jicoo, Inc.</>;
  const router = useRouter();

  return (
    <Footer>
      <Brand>
        <Link href={homePath()}>
          <ServiceLogo width={130} height={34} isDarkable />
        </Link>
      </Brand>
      <LinkList>
        <List>
          <ListItem>
            <ListItemLink href={termsPath()} rel="nofollow">
              {t('Terms')}
            </ListItemLink>
          </ListItem>
          {router.locale === jaLocale && (
            <ListItem>
              <ListItemLink href={tokushohoPath()} rel="nofollow">
                {t('Tokushoho')}
              </ListItemLink>
            </ListItem>
          )}
          <ListItem>
            <ListItemLink href={privacyPolicyPath()} rel="nofollow">
              {t('Privacy Policy')}
            </ListItemLink>
          </ListItem>
          {router.locale === jaLocale && (
            <ListItem>
              <ListItemLink href={securityPolicyPath()} rel="nofollow">
                {t('Security')}
              </ListItemLink>
            </ListItem>
          )}
          <ListItem>
            <ListItemLink href={aboutPath()}>{t('Company')}</ListItemLink>
          </ListItem>
        </List>
        <Copyrights>{copyRight}</Copyrights>
      </LinkList>
    </Footer>
  );
});

const Footer = styled.footer`
  padding: ${spacing(24)};
  margin-bottom: ${spacing(64)};
  ${mediaMin.tablet} {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
`;

const Brand = styled.div`
  text-align: center;
  ${mediaMin.tablet} {
    text-align: left;
  }
  margin: ${spacing(24)} 0;
`;
const LinkList = styled.div``;

const ServiceLogo = styled(Logo)`
  width: 130px;
`;

const List = styled.div`
  display: flex;
  align-items: center;
  ${mediaMin.tablet} {
    margin-left: auto;
  }
  ${mediaMax.tablet} {
    justify-content: center;
  }
  ${mediaMax.smartphone} {
    flex-wrap: wrap;
  }
`;
const ListItem = styled.div`
  text-align: center;
  margin-right: ${spacing(16)};
  :last-of-type {
    margin-right: 0;
  }
  ${mediaMax.smartphone} {
    width: 50%;
    margin-right: 0;
  }
`;
const ListItemLink = styled(Link)`
  color: ${gray01};
  font-size: ${fontSize(11)};
  font-weight: ${fontWeight('bold')};
  &:hover {
    color: ${black};
  }
  ${mediaMax.smartphone} {
    padding: ${spacing(8)};
    display: block;
  }
`;

const Copyrights = styled.div`
  margin-top: ${spacing(8)};
  color: ${gray01};
  font-size: ${fontSize(10)};
  font-weight: ${fontWeight('bold')};
  text-align: right;
  ${mediaMax.tablet} {
    text-align: center;
  }
`;

export default Component;
